import { jsx as _jsx } from "react/jsx-runtime";
import { getTypeFilter } from '@iotv/core-services';
import { getNiceDate, getParentEdgeTypeId, GroupClassName, Incident, IncidentClassName, IncidentStatus, isClassDerivedDeviceEnabledType, Ruleset, RulesetClassName, } from '@iotv/datamodel';
import { AdjacentType, AppValueTypes } from '@iotv/iotv-v3-types';
import { Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import { ContainerArbitrator } from '../../../../../ArbitrationObjects/ContainerArbitrator/containerArbitrator';
import { ducktype } from '../../../../../data/TypeHelpers';
import { GROUPING_PSEUDO_ROW_TYPE_ID } from './QueryTableContext';
const { REACT_APP_DEBUG } = process.env;
const debug = REACT_APP_DEBUG && false;
/** https://stackoverflow.com/a/27865285 */
function getPrecision(value) {
    let n = value;
    if (typeof n !== 'number')
        n = parseFloat(n);
    if (isNaN(n))
        return 0;
    let e = 1;
    while (Math.round(n * e) / e !== n)
        e *= 10;
    return Math.log(e) / Math.LN10;
}
export const nullExtractor = (_row, _contextObject, _context) => () => {
    _contextObject.type !== GroupClassName && console.info(`Null extractor called for row with type ${_contextObject?.type}: `);
    return null;
};
/** Don't evaluate against context for these types */
const blacklist = [GroupClassName, IncidentClassName];
export const rulesetsExtractor = (row, contextObject, context) => {
    if (blacklist.includes(contextObject.type))
        return () => null;
    if (!isClassDerivedDeviceEnabledType(contextObject))
        return () => null;
    const rulesetRes = context.queryByGraphSteps([contextObject], [
        { objectTypeId: GroupClassName, adjacencyType: AdjacentType.PARENT, edgeTypeId: undefined },
        { objectTypeId: RulesetClassName, adjacencyType: AdjacentType.PARENT, edgeTypeId: getParentEdgeTypeId(RulesetClassName, GroupClassName) },
    ]);
    const rulesetsFromGroups = rulesetRes.getNodesOfType(RulesetClassName).map((r) => new Ruleset(r));
    const appliesToTypeRulesets = context
        .getNodesOfType(RulesetClassName)
        .filter((ruleset) => {
        const r = ruleset;
        return r.appliesToType === contextObject.type;
    })
        .map((r) => new Ruleset(r));
    const rulesets = [...new Set([...rulesetsFromGroups, ...appliesToTypeRulesets])];
    const readingsAttributeMatchedRulesets = rulesets.filter((ruleset) => {
        const { BooleanRule: BooleanRules, MetricRule: MetricRules, ComparisonRule: ComparisonRules } = ruleset.definition;
        const possibleRules = [...(BooleanRules ?? []), ...(MetricRules ?? []), ...(ComparisonRules ?? [])];
        if (possibleRules.length === 0)
            return false;
        const matchedMetricRules = possibleRules.filter((rule) => {
            const { key } = rule;
            debug &&
                console.log(`Evaluating ruleset ${ruleset.name} rule ${rule.name} with key ${key} against row readingsAttributeKey ${row.readingsAttributeKey}`, rule);
            return key === row.readingsAttributeKey;
        });
        return 0 < matchedMetricRules.length;
    });
    return (_params) => (_jsx(Stack, { width: "100%", height: "100%", flexWrap: "nowrap", justifyContent: "center", alignItems: "center", children: readingsAttributeMatchedRulesets.length
            ? readingsAttributeMatchedRulesets.map((ruleset) => (_jsx(Link, { to: ContainerArbitrator.getCanonicalContainerLinkForObject({ vob: ruleset }), style: { color: 'inherit' }, children: ruleset.name }, ruleset.sk)))
            : '-' }));
};
export const incidentExtractor = (row, contextObject, context) => {
    if (blacklist.includes(contextObject.type)) {
        return () => null;
    }
    else {
        const incidentFilter = getTypeFilter(IncidentClassName);
        const incidents = context
            .getPseudoAttributes(contextObject)
            .filter(incidentFilter)
            .map((i) => new Incident(i));
        const openIncidents = incidents.filter((incident) => incident.status === IncidentStatus.OPEN);
        const incidentLength = openIncidents.length;
        return (_params) => (_jsx(Stack, { width: "100%", height: "100%", justifyContent: "center", alignItems: "center", children: incidentLength }));
    }
};
export const notificationExtractor = (row, contextObject, context) => {
    if (blacklist.includes(contextObject.type)) {
        return () => null;
    }
    else {
        const incidentFilter = getTypeFilter(IncidentClassName);
        const incidents = context
            .getPseudoAttributes(contextObject)
            .filter(incidentFilter)
            .map((i) => new Incident(i));
        const notifications = incidents
            .map((incident) => incident.notificationRecords)
            .flatMap((record) => (record ? Object.values(record) : []))
            .flat()
            .sort((a, b) => a.timestampMs - b.timestampMs);
        const latestNotification = notifications.at(0);
        return (_params) => (_jsx(Stack, { width: "100%", height: "100%", justifyContent: "center", alignItems: "center", children: latestNotification ? getNiceDate(latestNotification.timestampMs) : '-' }));
    }
};
export const attributeExtractor = (objectTypeId, attributeKey, nonScoreKey) => (row, contextObject, context) => {
    if (row.type === GROUPING_PSEUDO_ROW_TYPE_ID) {
        return () => null;
    }
    else if (contextObject.type === objectTypeId) {
        const value = contextObject[attributeKey];
        const duckType = ducktype(attributeKey, value);
        debug &&
            console.log(`attributeExtractor for ${attributeKey} called with contextObject type ${contextObject.type} duckType ${duckType} `, { contextObject });
        switch (duckType) {
            case AppValueTypes.BOOLEAN: {
                const typed = value;
                return () => (_jsx(Stack, { width: "100%", height: "100%", justifyContent: "center", alignItems: "center", children: typed ? 'True' : 'False' }));
            }
            case AppValueTypes.STRING: {
                const typed = value;
                return () => (_jsx(Stack, { width: "100%", height: "100%", justifyContent: "center", alignItems: "center", children: typed || '-' }));
            }
            case AppValueTypes.NUMBER: {
                const typed = value;
                let precision = 0;
                if (nonScoreKey)
                    precision = getPrecision(contextObject[nonScoreKey]);
                return () => (_jsx(Stack, { width: "100%", height: "100%", justifyContent: "center", alignItems: "center", children: typed.toLocaleString(undefined, { maximumFractionDigits: precision }) }));
            }
            case AppValueTypes.TIMESTAMP_MS: {
                const typed = value;
                return () => (_jsx(Stack, { width: "100%", height: "100%", justifyContent: "center", alignItems: "center", children: getNiceDate(typed) }));
            }
        }
        return () => (_jsx(Stack, { width: "100%", height: "100%", justifyContent: "center", alignItems: "center", children: "-" }));
    }
    else {
        return () => null;
    }
};
